import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/apiService';
import {
  LayoutDashboard, Users, CreditCard, Banknote,
  ClipboardList, FileText, AlertTriangle, BarChart3,
  PanelLeftClose, PanelLeftOpen, LogOut, Skull, Search, X, BookOpen, UserCircle, Shield, MessageSquareWarning, UserCog,
} from 'lucide-react';

const NAV_ITEMS = [
  { title: 'Dashboard',              path: '/',                       icon: LayoutDashboard },
  { title: 'Contributions Overview', path: '/contributions-overview', icon: ClipboardList },
  { title: 'Members',               path: '/members',                icon: Users },
  { title: 'Contributions',         path: '/payments',               icon: CreditCard },
  { title: 'Deceased',              path: '/deceased',               icon: Skull },
  { title: 'Notes',                 path: '/notes',                  icon: FileText },
  { title: 'Analytics',             path: '/analytics',              icon: BarChart3 },
  { title: 'Audit Log',             path: '/audit-log',              icon: Shield,                minRole: 'super_user' },
  { title: 'Reported Issues',       path: '/reported-issues',        icon: MessageSquareWarning,  minRole: 'super_user' },
  { title: 'Users',                 path: '/users',                  icon: UserCog,               minRole: 'super_user' },
];

function NavItem({ item, isCollapsed, isActive }) {
  const content = (
    <div className={`flex items-center gap-4 px-4 py-2.5 rounded-lg transition-colors duration-200 cursor-pointer
      ${isActive
        ? 'bg-slate-600 text-white shadow-sm'
        : 'text-slate-300 hover:bg-slate-700 hover:text-white'}`}>
      <item.icon className="w-5 h-5 flex-shrink-0" />
      {!isCollapsed && <span className="font-medium whitespace-nowrap text-sm">{item.title}</span>}
    </div>
  );
  return (
    <li className="mb-1">
      {isCollapsed ? (
        <div className="relative group">
          <Link to={item.path}>{content}</Link>
          <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 bg-slate-900 text-white text-xs rounded
            opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity">
            {item.title}
          </div>
        </div>
      ) : (
        <Link to={item.path}>{content}</Link>
      )}
    </li>
  );
}

function MemberSearch() {
  const [query, setQuery]     = useState('');
  const [results, setResults] = useState({ members: [], familyMembers: [] });
  const [open, setOpen]       = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate              = useNavigate();
  const wrapperRef            = useRef(null);
  const debounceRef           = useRef(null);

  const hasResults = results.members.length > 0 || results.familyMembers.length > 0;

  const search = useCallback(async (q) => {
    if (!q.trim()) { setResults({ members: [], familyMembers: [] }); setOpen(false); return; }
    setLoading(true);
    try {
      const data = await apiService.searchAll(q);
      setResults(data || { members: [], familyMembers: [] });
      setOpen(true);
    } catch { setResults({ members: [], familyMembers: [] }); }
    finally { setLoading(false); }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 300);
  };

  const handleSelectMember = (id) => {
    setQuery(''); setResults({ members: [], familyMembers: [] }); setOpen(false);
    navigate(`/members/${id}`);
  };

  const clear = () => { setQuery(''); setResults({ members: [], familyMembers: [] }); setOpen(false); };

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const statusDot = { active: 'bg-green-400', pending: 'bg-amber-400', inactive: 'bg-slate-400', rejected: 'bg-red-400' };

  const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '';

  return (
    <div ref={wrapperRef} className="relative w-full max-w-xs sm:max-w-sm">
      <div className="flex items-center gap-2 px-3 h-9 bg-white/10 hover:bg-white/15 focus-within:bg-white/20 rounded-lg transition-colors">
        <Search className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
        <input
          type="text"
          value={query}
          onChange={handleChange}
          onFocus={() => query && setOpen(true)}
          placeholder="Search members & family…"
          className="flex-1 bg-transparent text-white text-sm placeholder-slate-400 outline-none min-w-0"
        />
        {query && (
          <button onClick={clear} className="text-slate-400 hover:text-white transition-colors flex-shrink-0">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
        {loading && <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin flex-shrink-0" />}
      </div>

      {open && hasResults && (
        <div className="absolute top-full mt-1.5 left-0 right-0 bg-white rounded-xl shadow-2xl border border-slate-100 overflow-hidden z-50">

          {/* Account holders */}
          {results.members.length > 0 && (
            <>
              <p className="px-4 pt-2.5 pb-1 text-xs font-semibold text-slate-400 uppercase tracking-wide">Members</p>
              {results.members.map(m => (
                <button key={m.id} onMouseDown={() => handleSelectMember(m.id)}
                  className="w-full flex items-center gap-3 px-4 py-2 hover:bg-slate-50 transition-colors text-left">
                  <div className="relative flex-shrink-0">
                    <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-xs font-bold">
                      {m.name?.split(' ').slice(0, 2).map(n => n[0]).join('')}
                    </div>
                    <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border border-white ${statusDot[m.status] || 'bg-slate-400'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{m.name}</p>
                    <p className="text-xs text-slate-400">#{m.membershipRef}</p>
                  </div>
                </button>
              ))}
            </>
          )}

          {/* Family members */}
          {results.familyMembers.length > 0 && (
            <>
              <p className={`px-4 pb-1 text-xs font-semibold text-slate-400 uppercase tracking-wide ${results.members.length > 0 ? 'pt-2 border-t border-slate-100 mt-1' : 'pt-2.5'}`}>
                Family Members
              </p>
              {results.familyMembers.map(fm => (
                <button key={fm.id} onMouseDown={() => handleSelectMember(fm.holderId)}
                  className="w-full flex items-center gap-3 px-4 py-2 hover:bg-slate-50 transition-colors text-left">
                  <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 flex-shrink-0">
                    <UserCircle className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{fm.name}</p>
                    <p className="text-xs text-slate-400 truncate">{capitalize(fm.relation)} of {fm.holderName}</p>
                  </div>
                  <span className="text-xs text-slate-400 flex-shrink-0">{fm.holderRef}</span>
                </button>
              ))}
            </>
          )}

          <div className="h-1.5" />
        </div>
      )}

      {open && query && !loading && !hasResults && (
        <div className="absolute top-full mt-1.5 left-0 right-0 bg-white rounded-xl shadow-2xl border border-slate-100 px-4 py-3 z-50">
          <p className="text-sm text-slate-400">No results for "{query}"</p>
        </div>
      )}
    </div>
  );
}

const ROLE_LEVEL = { office_staff: 1, admin: 2, super_user: 3 };

export default function Layout({ children }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const initials = user?.username?.slice(0, 2).toUpperCase() || 'U';
  const userLevel = ROLE_LEVEL[user?.role] || 0;
  const visibleNav = NAV_ITEMS.filter(item => !item.minRole || userLevel >= ROLE_LEVEL[item.minRole]);

  return (
    <div className="h-screen flex flex-col bg-slate-50 overflow-hidden">
      {/* Header */}
      <header className="bg-gradient-to-r from-slate-800 to-blue-900 text-white h-14 flex items-center justify-between px-4 shadow-md z-30 flex-shrink-0 gap-3">

        {/* Left — logo + collapse */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            {/* Logo mark */}
            <BookOpen className="w-5 h-5 text-white flex-shrink-0" />
            {/* Wordmark */}
            <div className="hidden lg:block leading-none">
              <p className="text-white font-bold text-sm tracking-wide">AL-IMAN</p>
              <p className="text-white/60 text-[10px] font-medium tracking-wider uppercase">Funeral Management System</p>
            </div>
          </div>
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="hidden lg:flex items-center justify-center w-8 h-8 rounded-lg hover:bg-white/10 transition-colors text-white"
          >
            {isCollapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
          </button>
        </div>

        {/* Centre — member search */}
        <div className="flex-1 flex justify-center px-2">
          <MemberSearch />
        </div>

        {/* Right — user avatar + logout */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="hidden sm:flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-white/20 border border-white/30 flex items-center justify-center text-white text-xs font-semibold">
              {initials}
            </div>
            <span className="text-slate-200 text-sm font-medium">{user?.username}</span>
          </div>
          <button
            onClick={logout}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600/20 hover:bg-red-600/40 text-red-300 rounded-lg text-sm transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        <aside className={`bg-slate-800 text-white flex flex-col transition-all duration-300 ease-in-out
          ${isCollapsed ? 'w-16' : 'w-60'} hidden lg:flex flex-shrink-0`}>
          <nav className="flex-1 overflow-y-auto overflow-x-hidden p-3">
            <ul>
              {visibleNav.map(item => (
                <NavItem
                  key={item.path}
                  item={item}
                  isCollapsed={isCollapsed}
                  isActive={location.pathname === item.path}
                />
              ))}
            </ul>
          </nav>
        </aside>

        {/* Main content */}
        <main className="flex-1 overflow-y-auto bg-gradient-to-br from-slate-50 to-blue-50">
          {children}
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-slate-800 border-t border-slate-700 z-30">
        <div className="flex justify-around items-center h-14 px-2">
          {visibleNav.slice(0, 5).map(item => {
            const isActive = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path}
                className={`flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg transition-colors
                  ${isActive ? 'text-blue-400' : 'text-slate-400 hover:text-white'}`}>
                <item.icon className="w-5 h-5" />
                <span className="text-xs">{item.title.split(' ')[0]}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
